import React, { useState } from 'react';
import { BanknotesIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Vehicle, ExpenseCategory } from '../../types/vehicle';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface ExpenseRecorderProps {
  vehicle: Vehicle;
  onRecorded?: () => void;
}

const ExpenseRecorder: React.FC<ExpenseRecorderProps> = ({ vehicle, onRecorded }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    category: 'fuel' as ExpenseCategory,
    date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const expenseData: any = {
        vehicleId: vehicle.id,
        category: formData.category,
        date: new Date(formData.date),
        amount: parseFloat(formData.amount),
        description: formData.description,
        recordedBy: user.uid,
        createdAt: serverTimestamp(),
        organizationId: user.organizationId
      };

      // Csak akkor adjuk hozzá a notes-ot, ha van értéke
      if (formData.notes && formData.notes.trim()) {
        expenseData.notes = formData.notes.trim();
      }

      await addDoc(collection(db, 'vehicleExpenses'), expenseData);

      toast.success('Költség rögzítve');
      setIsOpen(false);
      setFormData({
        category: 'fuel',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        notes: ''
      });
      if (onRecorded) onRecorded();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const expenseCategories = [
    { value: 'fuel', label: 'Üzemanyag' },
    { value: 'insurance', label: 'Biztosítás' },
    { value: 'parking', label: 'Parkolás' },
    { value: 'toll', label: 'Útdíj' },
    { value: 'fine', label: 'Bírság' },
    { value: 'service', label: 'Szerviz' },
    { value: 'other', label: 'Egyéb' }
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
      >
        <PlusIcon className="h-4 w-4 mr-2" />
        Költség rögzítése
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Új költség rögzítése
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Kategória */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Kategória *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
            >
              {expenseCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Dátum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Dátum *
            </label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
            />
          </div>
        </div>

        {/* Összeg */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Összeg (HUF) *
          </label>
          <input
            type="number"
            required
            min="0"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            placeholder="0"
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        {/* Leírás */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Leírás *
          </label>
          <input
            type="text"
            required
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Pl. Tankolás 45 liter"
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        {/* Megjegyzés */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Megjegyzés
          </label>
          <textarea
            rows={2}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="További részletek..."
            className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setFormData({
                category: 'fuel',
                date: new Date().toISOString().split('T')[0],
                amount: '',
                description: '',
                notes: ''
              });
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {loading ? 'Mentés...' : 'Rögzítés'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseRecorder;
